import posthog from 'posthog-js';
import { getAttributionData } from './attribution';

let isInitialized = false;

/**
 * Initializes the PostHog Product & User Journey Analytics client.
 * Safe for all environments: if VITE_POSTHOG_KEY is not configured or in test/SSR,
 * runs gracefully in no-op mode without breaking application functionality.
 */
export function initPostHog(): void {
  if (typeof window === 'undefined' || isInitialized) return;

  const apiKey = import.meta.env.VITE_POSTHOG_KEY;
  const apiHost = import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com';

  if (!apiKey) {
    if (import.meta.env.DEV) {
      console.info('[PostHog] VITE_POSTHOG_KEY not set. Running in development no-op mode.');
    }
    return;
  }

  try {
    posthog.init(apiKey, {
      api_host: apiHost,
      autocapture: true,
      capture_pageview: true,
      capture_pageleave: true,
      persistence: 'localStorage+cookie',
      bootstrap: {
        distinctID: undefined,
        isIdentifiedID: false,
      },
      loaded: (ph) => {
        // Register persistent marketing attribution parameters
        const attribution = getAttributionData();
        if (attribution) {
          ph.register({
            utm_source: attribution.utm_source,
            utm_medium: attribution.utm_medium,
            utm_campaign: attribution.utm_campaign,
            utm_term: attribution.utm_term,
            utm_content: attribution.utm_content,
          });
        }
      },
    });

    isInitialized = true;
  } catch (err: any) {
    console.warn('[PostHog] Initialization notice:', err?.message);
  }
}

/**
 * Associates current session with an identified user, exhibitor, or attendee.
 */
export function identifyUser(
  userId: string,
  userProperties: {
    email?: string;
    name?: string;
    role?: 'attendee' | 'exhibitor' | 'speaker' | 'staff' | 'admin';
    tier?: string;
    company?: string;
    [key: string]: any;
  } = {}
): void {
  try {
    if (isInitialized) {
      posthog.identify(userId, userProperties);
    }
  } catch (err: any) {
    console.warn('[PostHog] Identify notice:', err?.message);
  }
}

/**
 * Clears user session on logout or break-glass reset.
 */
export function resetUser(): void {
  try {
    if (isInitialized) {
      posthog.reset();
    }
  } catch (err: any) {
    console.warn('[PostHog] Reset notice:', err?.message);
  }
}

export type JourneyType = 'visitor' | 'exhibitor' | 'speaker' | 'admin' | 'scanner';

export type JourneyStep =
  // Visitor Journey
  | 'visitor_landing_viewed'
  | 'visitor_tier_selected'
  | 'visitor_registration_started'
  | 'visitor_order_created'
  | 'visitor_payment_initiated'
  | 'visitor_payment_completed'
  | 'visitor_pass_accessed'
  // Exhibitor Journey
  | 'exhibitor_floorplan_viewed'
  | 'exhibitor_stall_selected'
  | 'exhibitor_stall_hold_requested'
  | 'exhibitor_stall_locked'
  | 'exhibitor_checkout_started'
  | 'exhibitor_payment_initiated'
  | 'exhibitor_payment_completed'
  | 'exhibitor_portal_accessed'
  // Speaker Journey
  | 'speaker_page_viewed'
  | 'speaker_nomination_submitted'
  | 'speaker_confirmed'
  // Staff & Admin Journey
  | 'admin_dashboard_accessed'
  | 'admin_stall_override'
  | 'scanner_badge_scanned'
  | 'scanner_check_in_success'
  | 'scanner_check_in_duplicate';

/**
 * Core Journey Funnel Tracker: records step transitions across user journeys.
 */
export function trackJourneyStep(
  journey: JourneyType,
  step: JourneyStep,
  properties: Record<string, any> = {}
): void {
  try {
    const payload = {
      journey,
      step,
      timestamp: new Date().toISOString(),
      ...getAttributionData(),
      ...properties,
    };

    if (isInitialized) {
      posthog.capture(`journey_${journey}_${step}`, payload);
    } else if (import.meta.env.DEV) {
      console.debug(`[PostHog Track: ${journey}]`, step, payload);
    }
  } catch (err: any) {
    console.warn(`[PostHog] Failed to track step '${step}':`, err?.message);
  }
}

/**
 * Tracks attendee delegate registration funnel actions.
 */
export function trackAttendeeRegistration(data: {
  tier: string;
  attendeeCount: number;
  totalAmountInr: number;
  orderId?: string;
  step: 'selected' | 'started' | 'order_created' | 'completed';
}): void {
  trackJourneyStep('visitor', `visitor_${data.step === 'completed' ? 'payment_completed' : 'registration_started'}` as JourneyStep, {
    tier: data.tier,
    attendeeCount: data.attendeeCount,
    totalAmountInr: data.totalAmountInr,
    orderId: data.orderId,
  });
}

/**
 * Tracks interactive floorplan stall interactions.
 */
export function trackStallAction(data: {
  stallId: string;
  action: 'click' | 'hold_requested' | 'locked' | 'checkout_started' | 'booked';
  priceInr?: number;
  companyName?: string;
  category?: string;
}): void {
  const stepMap: Record<string, JourneyStep> = {
    click: 'exhibitor_stall_selected',
    hold_requested: 'exhibitor_stall_hold_requested',
    locked: 'exhibitor_stall_locked',
    checkout_started: 'exhibitor_checkout_started',
    booked: 'exhibitor_payment_completed',
  };

  const step = stepMap[data.action] || 'exhibitor_stall_selected';
  trackJourneyStep('exhibitor', step, {
    stallId: data.stallId,
    priceInr: data.priceInr,
    companyName: data.companyName,
    category: data.category,
  });
}

/**
 * Tracks on-site gate check-in scanning actions.
 */
export function trackScannerEvent(data: {
  status: 'success' | 'duplicate' | 'invalid';
  tier?: string;
  qrToken?: string;
  scanTimeMs?: number;
}): void {
  const step: JourneyStep =
    data.status === 'success'
      ? 'scanner_check_in_success'
      : data.status === 'duplicate'
        ? 'scanner_check_in_duplicate'
        : 'scanner_badge_scanned';

  trackJourneyStep('scanner', step, {
    status: data.status,
    tier: data.tier,
    scanTimeMs: data.scanTimeMs,
  });
}

export default posthog;
